import { computed, ref, watch } from 'vue';

import type { CSSProperties, Ref } from 'vue';

export type UseImageZoomPanOptions = {
  /**
   * Whether zooming is available. While disabled the composable stays inert and
   * contributes no style, so the consumer's own cursor/transform rules stand.
   */
  isZoomEnabled: Ref<boolean>;
  /**
   * Whether a pointer drag may move the image. Panning is always off at the
   * minimum zoom, where the image already fits its viewport.
   */
  isPanEnabled: Ref<boolean>;
  minZoom?: number;
  maxZoom?: number;
  zoomStep?: number;
};

export type UseImageZoomPanReturn = {
  /** Bind to the element that clips the image; it bounds how far panning may go. */
  viewportRef: Ref<HTMLElement | null>;
  /** Bind to the image itself. */
  imageRef: Ref<HTMLImageElement | null>;
  zoom: Ref<number>;
  canZoomIn: Ref<boolean>;
  canZoomOut: Ref<boolean>;
  canPan: Ref<boolean>;
  imageStyle: Ref<CSSProperties | undefined>;
  zoomIn: () => void;
  zoomOut: () => void;
  reset: () => void;
  onPanStart: (event: PointerEvent) => void;
  onPanMove: (event: PointerEvent) => void;
  onPanEnd: (event: PointerEvent) => void;
};

/**
 * Zoom and pan mechanics for a previewed image. Kept out of the preview shell so
 * the shell only decides which controls to render, and so consumers that build
 * their own controls can drive the same behavior.
 */
export function useImageZoomPan({
  isZoomEnabled,
  isPanEnabled,
  minZoom = 1,
  maxZoom = 4,
  zoomStep = 0.5
}: UseImageZoomPanOptions): UseImageZoomPanReturn {
  const viewportRef = ref<HTMLElement | null>(null);
  const imageRef = ref<HTMLImageElement | null>(null);

  const zoom = ref(minZoom);
  const panX = ref(0);
  const panY = ref(0);
  const isPanning = ref(false);

  let panStart: { x: number; y: number; panX: number; panY: number } | null = null;

  const canZoomIn = computed(() => isZoomEnabled.value && zoom.value < maxZoom);
  const canZoomOut = computed(() => isZoomEnabled.value && zoom.value > minZoom);
  const canPan = computed(() => isPanEnabled.value && zoom.value > minZoom);

  const imageStyle = computed<CSSProperties | undefined>(() => {
    if (!isZoomEnabled.value && !isPanEnabled.value) {
      return undefined;
    }

    return {
      transform: `translate(${panX.value}px, ${panY.value}px) scale(${zoom.value})`,
      cursor: canPan.value ? (isPanning.value ? 'grabbing' : 'grab') : 'default'
    };
  });

  /**
   * Keeps the panned image within reach: at scale `z` the image overflows its
   * unscaled footprint by `size * (z - 1)`, so the furthest the center can move
   * while still covering an edge is half of that in each direction.
   */
  const clampPan = () => {
    const image = imageRef.value;
    const container = viewportRef.value;

    if (!image || !container) {
      return;
    }

    const maxX = Math.max(0, (image.offsetWidth * zoom.value - container.clientWidth) / 2);
    const maxY = Math.max(0, (image.offsetHeight * zoom.value - container.clientHeight) / 2);

    panX.value = Math.min(Math.max(panX.value, -maxX), maxX);
    panY.value = Math.min(Math.max(panY.value, -maxY), maxY);
  };

  const zoomIn = () => {
    if (!canZoomIn.value) {
      return;
    }

    zoom.value = Math.min(zoom.value + zoomStep, maxZoom);
  };

  const zoomOut = () => {
    if (!canZoomOut.value) {
      return;
    }

    zoom.value = Math.max(zoom.value - zoomStep, minZoom);
  };

  const reset = () => {
    zoom.value = minZoom;
    panX.value = 0;
    panY.value = 0;
    isPanning.value = false;
    panStart = null;
  };

  const onPanStart = (event: PointerEvent) => {
    if (!canPan.value) {
      return;
    }

    isPanning.value = true;
    panStart = { x: event.clientX, y: event.clientY, panX: panX.value, panY: panY.value };
    imageRef.value?.setPointerCapture?.(event.pointerId);
  };

  const onPanMove = (event: PointerEvent) => {
    if (!isPanning.value || !panStart) {
      return;
    }

    panX.value = panStart.panX + (event.clientX - panStart.x);
    panY.value = panStart.panY + (event.clientY - panStart.y);
    clampPan();
  };

  const onPanEnd = (event: PointerEvent) => {
    if (!isPanning.value) {
      return;
    }

    isPanning.value = false;
    panStart = null;
    imageRef.value?.releasePointerCapture?.(event.pointerId);
  };

  // Zooming out can shrink the reachable area; re-clamp so the image never
  // lingers off-center once it fits again.
  watch(zoom, () => clampPan());

  return {
    viewportRef,
    imageRef,
    zoom,
    canZoomIn,
    canZoomOut,
    canPan,
    imageStyle,
    zoomIn,
    zoomOut,
    reset,
    onPanStart,
    onPanMove,
    onPanEnd
  };
}
